'use client';

import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PriceActivityCategorySummary } from '@/lib/price-activity-types';

type PriceActivitySummaryProps = {
  summary: PriceActivityCategorySummary;
};

export function PriceActivitySummary({ summary }: PriceActivitySummaryProps) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      <SummaryChip
        icon={<ArrowUp className="h-3.5 w-3.5" aria-hidden />}
        label="aumentaram"
        value={summary.upCount}
        tone="up"
      />
      <SummaryChip
        icon={<ArrowDown className="h-3.5 w-3.5" aria-hidden />}
        label="diminuíram"
        value={summary.downCount}
        tone="down"
      />
      <SummaryChip
        icon={<Circle className="h-3 w-3 fill-current" aria-hidden />}
        label="alterações recentes"
        value={summary.total}
        tone="neutral"
      />
    </div>
  );
}

function SummaryChip({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  tone: 'up' | 'down' | 'neutral';
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-xl border border-solid px-3 py-2.5',
        'bg-white/[0.04] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]',
        tone === 'up' && 'border-emerald-400/25',
        tone === 'down' && 'border-rose-400/25',
        tone === 'neutral' && 'border-sky-400/20'
      )}
    >
      <span
        className={cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-lg',
          tone === 'up' && 'bg-emerald-500/15 text-emerald-300',
          tone === 'down' && 'bg-rose-500/15 text-rose-300',
          tone === 'neutral' && 'bg-sky-500/15 text-sky-200'
        )}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="font-poppins text-sm font-bold tabular-nums text-sky-50">{value}</p>
        <p className="font-inter text-[11px] text-sky-200/55">{label}</p>
      </div>
    </div>
  );
}
